import { useState, useCallback, useEffect } from 'react'
import { useUserLocalStorage } from '../hooks/useUserLocalStorage'
import { generateId } from '../utils'
import ApiService from '../services/ApiService'
import { ConversationContext } from './ConversationContext'

export function ConversationProvider({ children }) {
  const [convos, setConvos] = useUserLocalStorage('replysense-convos', [])
  const [currentConvo, setCurrentConvo] = useUserLocalStorage('replysense-current', null)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, convoId: null, convoTitle: '' })

  const newChat = useCallback(() => {
    const id = generateId()
    const convo = {
      id,
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
    }
    setConvos((prev) => [convo, ...prev])
    setCurrentConvo(id)
  }, [setConvos, setCurrentConvo])

  const deleteConvo = useCallback((convoId) => {
    setConvos((prev) => {
      const updatedConvos = prev.filter(convo => convo.id !== convoId)

      if (currentConvo === convoId) {
        if (updatedConvos.length > 0) {
          setCurrentConvo(updatedConvos[0].id)
        } else {
          const newId = generateId()
          const newConvo = {
            id: newId,
            title: 'New Chat',
            messages: [],
            createdAt: Date.now(),
          }
          setCurrentConvo(newId)
          return [newConvo]
        }
      }

      return updatedConvos
    })
  }, [currentConvo, setConvos, setCurrentConvo])

  const requestDeleteConvo = useCallback((convoId, convoTitle) => {
    setDeleteModal({ isOpen: true, convoId, convoTitle })
  }, [])

  const confirmDeleteConvo = useCallback(() => {
    deleteConvo(deleteModal.convoId)
    setDeleteModal({ isOpen: false, convoId: null, convoTitle: '' })
  }, [deleteConvo, deleteModal.convoId])

  const cancelDeleteConvo = useCallback(() => {
    setDeleteModal({ isOpen: false, convoId: null, convoTitle: '' })
  }, [])

  const sendMessage = useCallback(async (messageText) => {
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: messageText,
      timestamp: Date.now(),
    }

    try {
      const prediction = await ApiService.predict(messageText)
      const predictionLabel = prediction.fallback?.label || prediction.basic_prediction?.label || prediction.prediction || prediction.final_prediction || 'Unknown'
      const titleCaseLabel = predictionLabel.charAt(0).toUpperCase() + predictionLabel.slice(1).toLowerCase()

      const assistantMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Classification: ${titleCaseLabel}`,
        prediction,
        timestamp: Date.now(),
      }

      setConvos((prev) =>
        prev.map((convo) =>
          convo.id === currentConvo
            ? {
                ...convo,
                messages: [...convo.messages, userMessage, assistantMessage],
                title: convo.messages.length === 0 ? messageText.slice(0, 30) : convo.title,
              }
            : convo
        )
      )

      return { success: true }
    } catch (error) {
      const errorMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Error: ${error.response?.data?.message || error.message || 'Prediction failed'}`,
        timestamp: Date.now(),
      }

      setConvos((prev) =>
        prev.map((convo) =>
          convo.id === currentConvo
            ? {
                ...convo,
                messages: [...convo.messages, userMessage, errorMessage],
                title: convo.messages.length === 0 ? messageText.slice(0, 30) : convo.title,
              }
            : convo
        )
      )

      return { success: false, error }
    }
  }, [currentConvo, setConvos])

  // Initialize with a conversation if none exist
  useEffect(() => {
    if (convos.length === 0) {
      newChat()
    }
  }, [convos.length, newChat])

  const currentConvoData = convos.find((c) => c.id === currentConvo)

  const value = {
    convos,
    currentConvo,
    currentConvoData,
    deleteModal,
    newChat,
    deleteConvo,
    requestDeleteConvo,
    confirmDeleteConvo,
    cancelDeleteConvo,
    sendMessage,
    setCurrentConvo
  }

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  )
}